import type { CasAssignedStudent } from '~/@entities/Cas'
import type { LessonClassId } from '~/@entities/LessonClass'
import type { WorksheetReportPdfParams } from '~/@entities/Report'
import type { Student } from '~/@entities/Student'
import type { TeacherId } from '~/@entities/Teacher'
import type { Workbook } from '~/@entities/Workbook'
import type { Worksheet } from '~/@entities/Worksheet'
import { httpClient, type RequestMeta } from '~/http/client'

import { adaptPrintAgents, adaptRemotePrintResult } from './adapters'
import type { PrintAgent, RemotePrintOrder, RemotePrintResult } from './schemas'

// 인쇄 서버는 Swagger 를 내지 않아 요청 본문 타입을 여기서 직접 정의하고 응답은 unknown 으로 받는다.
// 환경별 주소·로컬 덮어쓰기·전용 트래킹은 앱이 꽂는 'print' 클라이언트가 맡는다.
// page 는 `1-3,5` 꼴의 쪽 범위이고 빈 문자열이면 서버가 전체 쪽을 찍는다.

/** 모든 인쇄 요청이 싣는 프린터·용지 설정 */
export type RemotePrintOption = {
  teacherId: TeacherId
  machineId: PrintAgent['machineId']
  printer: string
  isMonochrome: boolean
  isBooklet: boolean
  isDuplex: boolean
  isReverse: boolean
  // A4·A3·B4·B3
  paperType: string
  copyNumber: number
}

export type WorksheetPrintRequest = RemotePrintOption & {
  pieces: {
    pieceId: Worksheet['id']
    // 머리글에 찍을 학생 이름. 빈 배열이면 이름 없이 한 부만 찍는다
    names: string[]
    studentIds: Student['id'][]
    page: string
  }[]
  rangeOption: {
    problem: boolean
    quickAnswer: boolean
    answer: boolean
    omr: boolean
  }
  order?: RemotePrintOrder
}

export type ArithmeticWorksheetPrintRequest = RemotePrintOption & {
  page?: string
  pieces: { pieceId: Worksheet['id']; page: string }[]
}

export type WorkbookPrintRequest = RemotePrintOption & {
  page?: string
  books: { bookCode: Workbook['id']; page: string }[]
}

export type SchoolTestPrintRequest = RemotePrintOption & {
  // id 는 학교별 기출의 교재(schoolTestWorkbook) id 다
  tests: { id: number; page: string }[]
}

export type ReportPrintRequest = RemotePrintOption & {
  reports: { reportId: string; page: string }[]
}

export type RealTimeReportPrintRequest = RemotePrintOption & {
  reports: {
    page: string
    studentId: Student['id']
    year: number
    month: number
  }[]
}

export type OneClickReportPrintRequest = RemotePrintOption & {
  reports: {
    studentWorksheetId: string
    page: string
    reportRankOptions?: WorksheetReportPdfParams['reportRankOptions']
    lessonClassId?: LessonClassId
  }[]
}

export type CasReportPrintRequest = RemotePrintOption & {
  reports: { assignedCasId: CasAssignedStudent['assignedId']; page: string }[]
}

/***************** 요청 *****************/
/** 선생님 PC 에 등록된 인쇄 에이전트들. 응답은 { data: { agents } } 다 */
export const getPrintAgents = async (
  teacherId: RemotePrintOption['teacherId'],
  meta?: RequestMeta,
): Promise<PrintAgent[]> => {
  const body = await httpClient('print').get<{ data?: { agents?: unknown } }>(
    `/agents/${teacherId}`,
    { meta },
  )
  return adaptPrintAgents(body?.data?.agents)
}

const _print = async (path: string, body: unknown, meta?: RequestMeta) =>
  adaptRemotePrintResult(await httpClient('print').post<unknown>(path, body, { meta }))

/** 학습지 낱장 인쇄(연산 학습지도 같은 경로다) */
export const printWorksheet = (
  body: WorksheetPrintRequest | ArithmeticWorksheetPrintRequest,
  meta?: RequestMeta,
): Promise<RemotePrintResult> => _print('/print/mqtt/piece', body, meta)

export const printSchoolTests = (
  body: SchoolTestPrintRequest,
  meta?: RequestMeta,
): Promise<RemotePrintResult> => _print('/print/mqtt/school-tests', body, meta)

export const printCustomWorkbook = (
  body: WorkbookPrintRequest,
  meta?: RequestMeta,
): Promise<RemotePrintResult> => _print('/print/mqtt/custombook', body, meta)

/** 교재 빠른 정답 */
export const printCustomWorkbookQuickAnswer = (
  body: WorkbookPrintRequest,
  meta?: RequestMeta,
): Promise<RemotePrintResult> => _print('/print/custombook-quick', body, meta)

export const printReport = (
  body: ReportPrintRequest,
  meta?: RequestMeta,
): Promise<RemotePrintResult> => _print('/print/mqtt/report', body, meta)

export const printRealTimeReport = (
  body: RealTimeReportPrintRequest,
  meta?: RequestMeta,
): Promise<RemotePrintResult> => _print('/print/mqtt/report-realtime', body, meta)

export const printOneClickReport = (
  body: OneClickReportPrintRequest,
  meta?: RequestMeta,
): Promise<RemotePrintResult> => _print('/print/report-one', body, meta)

export const printCasReport = (
  body: CasReportPrintRequest,
  meta?: RequestMeta,
): Promise<RemotePrintResult> => _print('/print/mqtt/report-cas', body, meta)
